import { createSlice, CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import { Toast } from '@10d/tend-ui/primitives';

import { get, del, put, post } from 'api/requests';

import { AppThunk } from 'store/types';
import { IParamsReq } from 'types';
import { CallEngUnallocated } from 'pages/Calls/EngineeringCalls/types';

interface CallsStore {
  list: CallEngUnallocated[];
  loading: boolean;
  count: number;
}

const initialState: CallsStore = {
  list: [],
  loading: false,
  count: 0,
};

const reset: CaseReducer<CallsStore> = () => initialState;

const loading: CaseReducer<CallsStore, PayloadAction<boolean>> = (state, action) => {
  state.loading = action.payload;
};

const count: CaseReducer<CallsStore, PayloadAction<number>> = (state, action) => {
  state.count = action.payload;
};

const list: CaseReducer<CallsStore, PayloadAction<CallEngUnallocated[]>> = (state, action) => {
  state.list = action.payload;
};

export const unAllocatedSlice = createSlice({
  name: 'calls_engineeringNetworks_unAllocated',
  initialState,
  reducers: {
    reset,
    loading,
    list,
    count,
  },
});

const {
  reset: setReset,
  loading: setLoading,
  list: setCurrentCalls,
  count: setCallsCount,
} = unAllocatedSlice.actions;

export { setReset };

export const setCalls =
  (calls: CallEngUnallocated[]): AppThunk =>
  dispatch => {
    dispatch(setCurrentCalls(calls));
  };

export const getEngineeringUnallocatedCalls =
  (params: IParamsReq): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<Response>('/api/timesheet/engineering_appointment/in_progress/', params)
      .then(({ data }) => {
        const { count, results } = data;
        dispatch(setCalls(results));
        dispatch(setCallsCount(count));
      })
      .finally(() => dispatch(setLoading(false)));
  };

type Response = {
  count: number;
  results: CallEngUnallocated[];
};

export const deleteEngineeringUnallocatedCalls =
  (id: number): AppThunk =>
  (dispatch, getState) => {
    del(`/api/timesheet/engineering_appointment/${id}/cancel-appointment/`)
      .then(() => {
        dispatch(setLoading(true));
        const { calls } = getState();

        const updatedData = calls.engineeringNetworks.unAllocated.list.filter(
          calls => calls.id !== id,
        );
        const updatedCount = updatedData.length;

        dispatch(setCalls(updatedData));
        dispatch(setCallsCount(updatedCount));

        Toast.success({ message: 'Вызов успешно удален' });
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const editEngineeringUnallocatedCalls =
  (id: number, date: string): AppThunk =>
  (dispatch, getState) => {
    put(`/api/timesheet/engineering_appointment/${id}/edit_appointment_date/`, { date })
      .then(() => {
        dispatch(setLoading(true));
        const { calls } = getState();

        const reviewListCopy = [...calls.engineeringNetworks.unAllocated.list];
        const indexOfEditedReview = reviewListCopy.findIndex(review => review.id === id);
        const editedReview = reviewListCopy.splice(indexOfEditedReview, 1);
        const updatedReview = {
          ...editedReview[0],
          date: date,
        };
        reviewListCopy.push(updatedReview);
        reviewListCopy.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        dispatch(setCalls(reviewListCopy));

        Toast.success({ message: 'Вызов успешно перенесён' });
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const sendUnallocatedExcel = (reqData: IParamsReq): void => {
  post('/api/timesheet/engineering_appointment/in_progress/excel/', reqData);
};
